/**
 * Telemetry surface for the AI tier rollout. See docs/pricing/line-tier-integration-plan.md §6.
 *
 * The 8 canonical events named in the plan are enumerated below. Each has a
 * typed props shape so downstream dashboards can be built against a stable
 * contract. Emitting is fire-and-forget:
 *
 *   1. Try `VITE_AI_TIER_ANALYTICS_URL` if configured (POST JSON, no-await)
 *   2. Always mirror to `console.debug` when the flag is off, so devs still
 *      see the trail during local sessions.
 *
 * Server aggregation is authored later (Phase 2 rollout). This file gives
 * every call site a single import that will never throw and never block.
 */

import { lineFeatureFlags } from '../config/lineFeatureFlags'
import type { AiCallClass } from './aiRouter'
import type { AiModelId, AiTierId } from '../types/ai'

const endpoint = import.meta.env.VITE_AI_TIER_ANALYTICS_URL as string | undefined

export type AiTierAnalyticsEvent =
  | { name: 'line_tier_switcher_shown'; props: { plan: string | null; defaultTier: AiTierId; availableTiers: readonly AiTierId[] } }
  | { name: 'line_tier_switched'; props: { from: AiTierId; to: AiTierId; chatId?: string | null; plan: string | null } }
  | { name: 'line_tier_locked_hover'; props: { attemptedTier: AiTierId; plan: string | null } }
  | { name: 'line_tier_locked_click'; props: { attemptedTier: AiTierId; plan: string | null } }
  | { name: 'line_upsell_shown'; props: { trigger: 'locked_chip' | 'quota_exhausted'; attemptedTier: AiTierId } }
  | { name: 'line_upsell_converted'; props: { fromPlan: string | null; toPlan: string; trigger: 'locked_chip' | 'quota_exhausted' } }
  | { name: 'ai_call_dispatched'; props: { tier: AiTierId; modelUsed: AiModelId; callClass: AiCallClass; estimatedCredits: number } }
  | { name: 'ai_call_completed'; props: { tier: AiTierId; modelUsed: AiModelId; actualCredits: number; latencyMs: number; success: boolean; errorCode?: string | null } }

export type AiTierAnalyticsEventName = AiTierAnalyticsEvent['name']

export const trackAiTierEvent = <E extends AiTierAnalyticsEvent>(event: E): void => {
  const payload = { ...event, ts: Date.now(), flagEnabled: lineFeatureFlags.aiTierSwitcher }
  if (endpoint) {
    // fire and forget — never let analytics block the user
    void fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((error) => console.debug('[ai-tier-analytics] endpoint failure', error))
    return
  }
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[ai-tier-analytics]', payload)
  }
}
